import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { useEffect, useRef, useState } from 'react';
import {
    type Control,
    Controller,
    type FieldErrors,
    type UseFormClearErrors,
    type UseFormRegister,
    type UseFormSetValue,
    useWatch,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import CompanyShippingFields from '@/components/steps/ShippingAddressStep/CompanyShippingFields';
import ShippingLabelPreview from '@/components/steps/ShippingAddressStep/ShippingLabelPreview';
import {
    ADDRESS_TYPE,
    SHIPPING_FIELD_MAX_LENGTH,
    type ShippingSchemaType,
} from '@/components/steps/ShippingAddressStep/shippingSchema';
import CountedInput from '@/components/ui/CountedInput';
import Input from '@/components/ui/Input';
import SelectableCard from '@/components/ui/SelectableCard';
import SelectInput from '@/components/ui/SelectInput';
import { useCountryNames } from '@/hooks/useCountryNames';
import { useOptionalField } from '@/hooks/useOptionalField';
import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

type ShippingAddressFormProps = {
    state: ShippingSchemaType;
    setValue: UseFormSetValue<ShippingSchemaType>;
    clearErrors: UseFormClearErrors<ShippingSchemaType>;
    errors: FieldErrors<ShippingSchemaType>;
    register: UseFormRegister<ShippingSchemaType>;
    control: Control<ShippingSchemaType>;
    isRedesignEnabled: boolean;
};

const ShippingAddressForm = ({
    state,
    setValue,
    clearErrors,
    errors,
    register,
    control,
    isRedesignEnabled,
}: ShippingAddressFormProps) => {
    const [showAdditionalAddress, setShowAdditionalAddress] = useState(Boolean(state?.address2));
    const [showAddressee, toggleAddressee] = useOptionalField(setValue, 'addressee', Boolean(state?.addressee));

    const toggleAdditionalAddress = () => {
        setShowAdditionalAddress(prev => {
            if (prev) {
                setValue('address2', '');
            }
            return !prev;
        });
    };

    const countryNames = useCountryNames;
    const { account } = useUserStore();
    const addressType = useWatch({ control, name: 'addressType' }) ?? ADDRESS_TYPE.company;
    const addressee = useWatch({ control, name: 'addressee' });

    const companyAddressRef = useRef({
        address: account?.address?.firstAddressLine || '',
        address2: account?.address?.secondAddressLine || '',
        postCode: account?.address?.zip || '',
        city: account?.address?.city || '',
        country: account?.address?.countryCode || '',
    });

    useEffect(() => {
        if (account?.address && !companyAddressRef.current.address) {
            companyAddressRef.current = {
                address: account.address.firstAddressLine || '',
                address2: account.address.secondAddressLine || '',
                postCode: account.address.zip || '',
                city: account.address.city || '',
                country: account.address.countryCode || '',
            };
        }
    }, [account]);

    useEffect(() => {
        if (account?.name && !state.companyName) {
            setValue('companyName', account.name);
        }
    }, [account, state.companyName, setValue]);

    const handleSelectCompany = () => {
        setValue('addressType', ADDRESS_TYPE.company);
        const companyAddress = companyAddressRef.current;
        setValue('address', companyAddress.address);
        setValue('address2', companyAddress.address2);
        setValue('postCode', companyAddress.postCode);
        setValue('city', companyAddress.city);
        setValue('country', companyAddress.country);
        setShowAdditionalAddress(Boolean(companyAddress.address2));
        clearErrors();
    };

    const handleSelectIndividual = () => {
        setValue('addressType', ADDRESS_TYPE.individual);
        setValue('address', '');
        setValue('address2', '');
        setValue('postCode', '');
        setValue('city', '');
        setValue('country', '');
        setShowAdditionalAddress(false);
    };

    return (
        <>
            <div className='text-bold text-size-18 padding-bottom-15'>{t('onboarding.shippingAddress.title')}</div>
            <div className='padding-bottom-15'>
                <FormattedMessage id='onboarding.shippingAddress.description' values={{ br: <br /> }} />
            </div>

            {isRedesignEnabled && (
                <div className='display-flex gap-15 margin-bottom-15'>
                    <div className='flex-1-1-0'>
                        <span className='text-size-12 text-color-dark display-block margin-bottom-15'>
                            {t('onboarding.shippingAddress.cardDestination')}
                        </span>
                        <div className='display-flex gap-15'>
                            <SelectableCard
                                icon='factory'
                                title={t('onboarding.shippingAddress.company.title')}
                                description={t('onboarding.shippingAddress.company.description')}
                                selected={addressType === ADDRESS_TYPE.company}
                                onClick={handleSelectCompany}
                            />
                            <SelectableCard
                                icon='driver'
                                title={t('onboarding.shippingAddress.individual.title')}
                                description={t('onboarding.shippingAddress.individual.description')}
                                selected={addressType === ADDRESS_TYPE.individual}
                                onClick={handleSelectIndividual}
                            />
                        </div>
                    </div>
                    <ShippingLabelPreview control={control} />
                </div>
            )}

            {isRedesignEnabled && addressType === ADDRESS_TYPE.company && (
                <CompanyShippingFields
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    defaultShowRecipient={Boolean(addressee)}
                />
            )}

            {isRedesignEnabled && addressType === ADDRESS_TYPE.individual && (
                <>
                    <CountedInput
                        required
                        control={control}
                        name='addressee'
                        label={t('general.recipient')}
                        error={errors?.addressee?.message}
                        maxLength={SHIPPING_FIELD_MAX_LENGTH.recipient}
                        placeholder={t('onboarding.shippingAddress.recipient.placeholder')}
                    />
                    <hr className='margin-y-15' />
                </>
            )}

            <CountedInput
                required
                control={control}
                name='address'
                label={t('general.address')}
                error={errors?.address?.message}
                maxLength={SHIPPING_FIELD_MAX_LENGTH.address}
            />
            {showAdditionalAddress && (
                <CountedInput
                    control={control}
                    name='address2'
                    className='margin-top-15'
                    label={t('general.addressOptional')}
                    placeholder={t('general.addressOptional.placeholder')}
                    maxLength={SHIPPING_FIELD_MAX_LENGTH.address}
                />
            )}
            <button
                type='button'
                className='btn btn-link btn-primary width-auto align-self-start padding-5 margin-bottom-15'
                onClick={toggleAdditionalAddress}
            >
                <span className={`rioglyph ${showAdditionalAddress ? 'rioglyph-trash' : 'rioglyph-plus'}`} />
                <span>
                    {t(
                        showAdditionalAddress
                            ? 'general.removeAdditionalAddressLine'
                            : 'general.addAdditionalAddressLine'
                    )}
                </span>
            </button>

            <div className='display-flex gap-15'>
                <CountedInput
                    required
                    control={control}
                    name='postCode'
                    className='flex-1-1-0'
                    label={t('general.postCode')}
                    error={errors?.postCode?.message}
                    maxLength={SHIPPING_FIELD_MAX_LENGTH.postCode}
                />
                <CountedInput
                    required
                    control={control}
                    name='city'
                    className='flex-1-1-0'
                    label={t('general.city')}
                    error={errors?.city?.message}
                    maxLength={SHIPPING_FIELD_MAX_LENGTH.city}
                />
                <Controller
                    name='country'
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <SelectInput
                            required
                            className='flex-1-1-0 margin-bottom-0'
                            error={errors.country?.message}
                            label={t('general.country')}
                            selectAttributes={{
                                onChange: (item?: SelectOption) => onChange(item?.id),
                                options: countryNames(value),
                            }}
                        />
                    )}
                />
            </div>

            {/* Addressee */}
            {!isRedesignEnabled && (
                <>
                    <hr />
                    <p className='margin-bottom-0'>{t('general.addresseeFootnote')}</p>
                    {showAddressee && (
                        <div className='display-grid grid-cols-3 gap-15'>
                            <Input
                                className='margin-top-5'
                                label={t('general.addressee.optional')}
                                inputAttributes={{ ...register('addressee') }}
                            />
                        </div>
                    )}
                    <button
                        type='button'
                        className='btn btn-link btn-primary width-auto align-self-start padding-5'
                        onClick={toggleAddressee}
                    >
                        <span className={`rioglyph ${showAddressee ? 'rioglyph-trash' : 'rioglyph-plus'}`} />
                        <span>{t(showAddressee ? 'general.removeAddresseeLine' : 'general.addAddresseeLine')}</span>
                    </button>
                </>
            )}
        </>
    );
};

export default ShippingAddressForm;
